import React from "react";
import { useTranslation } from "react-i18next";
import { OrganizationalUnit, organizationalUnit } from "@planzone/dto";
import {
  useCreateOrganizationalUnit,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { ResourcesPicker } from "@common/resourcesPicker";
import { ParentSelection } from "./parentSelection";
import { Parent } from "./parentSelection/types";
import { Props } from "./types";
import styles from "./styles.scss";

function OrganizationalUnitCreation({ parent, cancel }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { isRequestPending, createOrganizationalUnit } =
    useCreateOrganizationalUnit();
  const [selectedParent, setSelectedParent] = React.useState<Parent>(parent);
  const [formData, setFormData] = React.useState<OrganizationalUnit>({
    ...organizationalUnit,
    resources: [],
  });
  const resources = formData.resources ?? [];

  return (
    <Modal
      header={t("obsPage.creation.title")}
      className={styles.organizationalUnitCreation}
      close={cancel}
    >
      <form
        className={styles.main}
        onSubmit={(e) => {
          e.preventDefault();

          !isRequestPending &&
            createOrganizationalUnit({
              planzone,
              organizationalUnit: {
                ...formData,
                parentUuid: selectedParent?.uuid,
              },
            });
        }}
      >
        <div className={styles.group}>
          <h3 className={styles.title}>{t("obsPage.creation.name")}</h3>
          <input
            className={styles.input}
            value={formData.name}
            placeholder={t("obsPage.creation.namePlaceholder")}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            autoFocus
          />
        </div>
        <div className={styles.group}>
          <h3 className={styles.title}>{t("obsPage.creation.parent")}</h3>
          <ParentSelection parent={selectedParent} select={setSelectedParent} />
        </div>
        <ResourcesPicker
          title={t("obsPage.creation.resources", { total: resources.length })}
          resources={resources}
          addResources={(added) =>
            setFormData({
              ...formData,
              resources: [
                ...resources.filter(
                  (resource) =>
                    !added.some(({ uuid }) => uuid === resource.uuid),
                ),
                ...added,
              ],
            })
          }
          removeResources={(removed) =>
            setFormData({
              ...formData,
              resources: resources.filter(
                (resource) =>
                  !removed.some(({ uuid }) => uuid === resource.uuid),
              ),
            })
          }
        />
        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("obsPage.creation.cancel")}
          </Button>
          <Button
            type="submit"
            status={isRequestPending ? "pending" : "none"}
            isDisabled={isRequestPending || !formData.name.trim()}
          >
            {t("obsPage.creation.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { OrganizationalUnitCreation };
